import { Box, useTheme } from '@mui/material';
import Chip from '@mui/material/Chip';
import { WorkflowStatus } from '../../types/WorkflowState';

export interface DataCoverageProps {
  percentage?: number;
  workflow_state?: WorkflowStatus;
}

export function DataCoverage({ percentage = 0, workflow_state }: DataCoverageProps) {
  const theme = useTheme();

  if (
    workflow_state !== WorkflowStatus.DUEDILLIGENCE_COMPLETED &&
    workflow_state !== WorkflowStatus.REPORT_COMPLETED
  ) {
    return (
      <Chip
        sx={{ bgcolor: theme.palette.grey[200] }}
        label="Not available for Screener"
        size="small"
      />
    );
  }

  const percentageRounded = Math.round(Math.max(0, Math.min(100, percentage * 100))) || 0;
  const color =
    percentageRounded <= 25
      ? theme.palette.error.main
      : percentageRounded > 25 && percentageRounded <= 50
        ? theme.palette.warning.main
        : theme.palette.success.main;
  const backgroundColor =
    percentageRounded <= 25 ? theme.palette.error.light : theme.palette.info.light;

  return (
    <Chip
      sx={{ bgcolor: backgroundColor }}
      label={`${percentageRounded}%`}
      icon={
        <DataCoverageIcon
          color={color}
          percentage={percentageRounded}
          bgColor={theme.palette.info.light}
        />
      }
    />
  );
}

interface DataCoverageIconProps {
  color: string;
  percentage: number;
  bgColor: string;
}
export function DataCoverageIcon({ color, percentage, bgColor }: DataCoverageIconProps) {
  const size = 24;
  const radius = 10;
  const center = size / 2;
  const angle = (percentage / 100) * 360;
  const radians = (angle - 90) * (Math.PI / 180);
  const x = center + radius * Math.cos(radians);
  const y = center + radius * Math.sin(radians);
  const largeArcFlag = percentage > 50 ? 1 : 0;

  // Path for the pie slice
  const pathData =
    percentage === 0
      ? ''
      : percentage === 100
        ? `
        M ${center} ${center}
        m -${radius}, 0
        a ${radius},${radius} 0 1,0 ${radius * 2},0
        a ${radius},${radius} 0 1,0 -${radius * 2},0
      `
        : `
        M ${center} ${center}
        L ${center} ${center - radius}
        A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x} ${y}
        Z
      `;

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
        m: 1,
        mr: 0,
      }}
    >
      <svg width={size} height={size}>
        <circle cx={center} cy={center} r={radius} fill={bgColor} />
        {percentage > 0 && <path d={pathData} fill={color} />}
        <circle cx={center} cy={center} r={radius} fill="none" stroke={color} strokeWidth={1.5} />
      </svg>
    </Box>
  );
}

export default DataCoverage;
